import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import FlexContainer from 'core-system/FlexContainer'
import Card from 'core-system/Card'
import ComplianceCard from 'features/ProgramRecommender/widgets/ComplianceCard'
import { mockComplianceData } from 'features/ProgramRecommender/mockData'
import palette from 'core-system/Themes/palette'
import SuccessIcon from 'core-system/Icons/Misc/Success'
import AlertIcon from 'core-system/Icons/Misc/Alert'

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`

const Header = styled.div`
  margin-bottom: 2rem;
`

const OverviewCard = styled(Card)`
  margin-bottom: 2rem;
  padding: 1.5rem;
`

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;

  @media (max-width: ${({ theme }) => theme.screenSize.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const StatItem = styled.div`
  text-align: center;
`

const StatValue = styled(Text)`
  font-size: 2.5rem;
  font-weight: 600;
  line-height: 1.2;
`

const ComplianceStatus = styled(FlexContainer)`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${palette.grey.grey300};
`

const StatusBadge = styled.div<{ isCompliant: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  background-color: ${({ isCompliant }) =>
    isCompliant ? palette.success.green100 : palette.error.red100};
  color: ${({ isCompliant }) =>
    isCompliant ? palette.success.green500 : palette.error.red500};
`

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: ${({ theme }) => theme.screenSize.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.screenSize.md}) {
    grid-template-columns: 1fr;
  }
`

// Calculate overview stats from mock data
const overviewStats = mockComplianceData.reduce(
  (acc, area) => ({
    totalAreas: acc.totalAreas + 1,
    totalSites: acc.totalSites + area.totalSites,
    compliantSites: acc.compliantSites + area.compliantSites,
    totalEmployees: acc.totalEmployees + area.totalEmployees,
  }),
  { totalAreas: 0, totalSites: 0, compliantSites: 0, totalEmployees: 0 }
)

const complianceRate = Math.round(
  (overviewStats.compliantSites / overviewStats.totalSites) * 100
)
const nonCompliantSites =
  overviewStats.totalSites - overviewStats.compliantSites

const ComplianceView = React.memo(() => {
  return (
    <PageContainer>
      <Header>
        <Text variant='h3' textColor='secondary'>
          Monitor and manage compliance requirements across your worksites
        </Text>
      </Header>

      <OverviewCard>
        <Text variant='h4' marginBottom='1.5rem'>
          Compliance Overview
        </Text>
        <OverviewGrid>
          <StatItem>
            <StatValue>{overviewStats.totalAreas}</StatValue>
            <Text variant='body2' textColor='secondary'>
              Regions
            </Text>
          </StatItem>
          <StatItem>
            <StatValue>{overviewStats.totalSites}</StatValue>
            <Text variant='body2' textColor='secondary'>
              Total Sites
            </Text>
          </StatItem>
          <StatItem>
            <StatValue color={palette.success.green500}>
              {overviewStats.compliantSites}
            </StatValue>
            <Text variant='body2' textColor='secondary'>
              Compliant Sites
            </Text>
          </StatItem>
          <StatItem>
            <StatValue>
              {overviewStats.totalEmployees.toLocaleString()}
            </StatValue>
            <Text variant='body2' textColor='secondary'>
              Total Employees
            </Text>
          </StatItem>
        </OverviewGrid>
        <ComplianceStatus justifyContent='space-between' alignItems='center'>
          <FlexContainer alignItems='center'>
            <Text variant='body1'>Overall Compliance Rate:&nbsp;</Text>
            <Text
              variant='h4'
              color={
                complianceRate >= 80
                  ? palette.success.green500
                  : palette.warning.orange500
              }
            >
              {complianceRate}%
            </Text>
          </FlexContainer>
          <FlexContainer gap='1rem'>
            <StatusBadge isCompliant={true}>
              <SuccessIcon color={palette.success.green500} />
              <Text variant='action4'>
                {overviewStats.compliantSites} Compliant
              </Text>
            </StatusBadge>
            {nonCompliantSites > 0 && (
              <StatusBadge isCompliant={false}>
                <AlertIcon color={palette.error.red500} />
                <Text variant='action4'>{nonCompliantSites} Action Needed</Text>
              </StatusBadge>
            )}
          </FlexContainer>
        </ComplianceStatus>
      </OverviewCard>

      <CardsContainer>
        {mockComplianceData.map((complianceData, index) => (
          <ComplianceCard key={index} {...complianceData} />
        ))}
      </CardsContainer>
    </PageContainer>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  ComplianceView.displayName = 'ComplianceView'
}

export default ComplianceView
